import React, { useEffect, useRef } from 'react';
import { ChatMessageItem } from './ChatMessageItem';

type Message = {
  id: string;
  idn?: string;
  sender: string;
  text: string;
  timestamp: string;
  imageUrl?: string;
  linkPreview?: {
    title: string;
    description: string;
    image?: string;
    url: string;
  };
  reactions?: { [userId: string]: string };
};

type Participant = {
  id: string;
  firstName: string;
  lastName: string;
  imageUrl?: string;
  online?: boolean;
};

type MessageListProps = {
  messages: Message[];
  userId: string;
  participants: Participant[];
  onDeleteMessage: (messageId: string) => void;
  onAddReaction: (messageId: string, emoji: string) => void;
};

export const ChatMessageList: React.FC<MessageListProps> = ({
  messages,
  userId,
  participants,
  onDeleteMessage,
  onAddReaction,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef<number>(0);

  // Improved scroll behavior for new messages
  useEffect(() => {
    // Only auto-scroll if we're already at the bottom or if new messages are added
    const shouldScrollToBottom = () => {
      if (!containerRef.current) return true;

      // Check if we're already near the bottom
      const { scrollHeight, scrollTop, clientHeight } = containerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

      // Or if new messages were added
      const newMessagesAdded = messages.length > prevMessagesLengthRef.current;
      prevMessagesLengthRef.current = messages.length;

      return isNearBottom || newMessagesAdded;
    };

    if (messagesEndRef.current && shouldScrollToBottom()) {
      // Use a small timeout to ensure DOM updates are complete
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 50);
    }
  }, [messages]);

  const getSenderInfo = (senderId: string) => {
    const participant = participants.find(p => p.id === senderId);
    return {
      name: participant?.firstName + ' ' + participant?.lastName || 'Unknown User4',
      avatar: participant?.imageUrl || '/assets/images/fallback.jpg'
    };
  };

  // Group messages by sender for consecutive messages
  const getShowSenderInfo = (index: number, message: Message) => {
    if (index === 0) return true;
    const prevMessage = messages[index - 1];

    // Show sender info if this is a different sender or if more than 5 minutes have passed
    if (prevMessage.sender !== message.sender) return true;

    const prevTime = new Date(prevMessage.timestamp).getTime();
    const currentTime = new Date(message.timestamp).getTime();
    const fiveMinutes = 5 * 60 * 1000;

    return currentTime - prevTime > fiveMinutes;
  };

  // Format timestamp for display in dividers
  const formatTimeDivider = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  };

  // Check if we should show a timestamp divider between messages
  const shouldShowTimeDivider = (index: number, message: Message) => {
    if (index === 0) return true; // Always show timestamp for first message

    const prevMessage = messages[index - 1];
    const prevTime = new Date(prevMessage.timestamp).getTime();
    const currentTime = new Date(message.timestamp).getTime();
    const tenMinutes = 10 * 60 * 1000;

    return currentTime - prevTime > tenMinutes;
  };

  return (
    <div className="h-full overflow-y-auto" ref={containerRef}>
      <div className="flex flex-col p-4">
        {
          messages.map((message, index) => {
            const isOtherUser = message.sender !== userId;
            const { name, avatar } = getSenderInfo(message.sender);
            const showSenderInfo = getShowSenderInfo(index, message);
            const showTimeDivider = shouldShowTimeDivider(index, message);

            return (
              <React.Fragment key={message.id}>
                {showTimeDivider && (
                  <div className="my-2 flex justify-center">
                    <div className="rounded-full bg-secondary/20 px-3 py-1 text-xs text-muted-foreground dark:bg-neutral-700/30">
                      {formatTimeDivider(message.timestamp)}
                    </div>
                  </div>
                )}
                <div
                  ref={index === messages.length - 1 ? messagesEndRef : undefined}
                  className="group"
                >
                  <ChatMessageItem
                    message={message}
                    userId={userId}
                    isOtherUser={isOtherUser}
                    senderName={name}
                    senderAvatar={avatar}
                    showSenderInfo={showSenderInfo}
                    onDeleteMessage={onDeleteMessage}
                    onAddReaction={onAddReaction}
                    hideTimestamp={true}
                  />
                </div>
              </React.Fragment>
            );
          })
        }
        {/* Empty div at the end for scroll target */}
        <div ref={messagesEndRef} style={{ height: '1px', width: '100%' }} />
      </div>
    </div>
  );
};
